import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { User, LogOut, CreditCard, ChevronDown, Settings, HelpCircle, Sun, Moon, SunMoon, Crown, Award, RefreshCw, Sparkles } from "lucide-react";
import { useAuth } from "../helpers/useAuth";
import { useLicenseAccess } from "../helpers/useLicenseAccess";
import { useThemeMode, type ThemeMode } from "../helpers/themeMode";
import { useInlineCheckout } from "./InlineCheckoutDialog";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from "./DropdownMenu";
import { Skeleton } from "./Skeleton";
import styles from "./AppUserMenu.module.css";

const ThemeSelector = () => {
  const { mode, switchToLightMode, switchToDarkMode, switchToAutoMode } = useThemeMode();

  const options: { value: ThemeMode; label: string; icon: React.ReactNode }[] = [
    { value: "light", label: "Clair", icon: <Sun size={14} /> },
    { value: "dark", label: "Sombre", icon: <Moon size={14} /> },
    { value: "auto", label: "Auto", icon: <SunMoon size={14} /> },
  ];

  const handleClick = (value: ThemeMode) => {
    if (value === "light") switchToLightMode();
    else if (value === "dark") switchToDarkMode();
    else switchToAutoMode();
  };

  return (
    <div className={styles.themeSelector}>
      {options.map(({ value, label, icon }) => (
        <button
          key={value}
          className={`${styles.themeBtn} ${mode === value ? styles.themeBtnActive : ""}`}
          onClick={() => handleClick(value)}
        >
          {icon}
          <span>{label}</span>
        </button>
      ))}
    </div>
  );
};

const UpgradeMenuItem = () => {
  const { isFreeUser, hasPremiumAccess, postTrial, licenseStatus } = useLicenseAccess();
  const { openCheckout } = useInlineCheckout();

  const isInTrial = licenseStatus?.isInTrial ?? false;
  const trialDays = licenseStatus?.trialDaysRemaining ?? 0;
  // Date de fin lisible (« 20 octobre ») : parade à la confusion compte gratuit / essai.
  const trialEndsAt = licenseStatus?.trialEndsAt ? new Date(licenseStatus.trialEndsAt) : null;
  const trialEndLabel = trialEndsAt && !Number.isNaN(trialEndsAt.getTime())
    ? new Intl.DateTimeFormat("fr-FR", { day: "numeric", month: "long" }).format(trialEndsAt)
    : null;
  const hasPurchasedBefore = licenseStatus?.hasPurchasedBefore ?? false;
  const isSubscriptionActive = licenseStatus?.isSubscriptionActive ?? false;

  // Premium active → link to account
  if (hasPremiumAccess && !isInTrial) {
    return (
      <DropdownMenuItem asChild>
        <Link to="/compte/">
          <Award size={16} className={styles.menuIcon} />
          Premium actif
        </Link>
      </DropdownMenuItem>
    );
  }

  // In trial → link to tarifs with days remaining
  if (isInTrial) {
    return (
      <DropdownMenuItem asChild>
        <Link to="/tarifs/" className={styles.trialItem}>
          <Crown size={16} className={styles.menuIcon} />
          {trialEndLabel ? `Premium offert jusqu'au ${trialEndLabel}` : `Premium offert, J-${trialDays}`}
        </Link>
      </DropdownMenuItem>
    );
  }

  // Expired subscription (has purchased before but not active)
  if (hasPurchasedBefore && !isSubscriptionActive) {
    return (
      <DropdownMenuItem onClick={openCheckout} className={styles.renewItem}>
        <RefreshCw size={16} className={styles.menuIcon} />
        Renouveler mon abonnement
      </DropdownMenuItem>
    );
  }

  // Free or post-trial → highlight upgrade
  if (isFreeUser || postTrial) {
    return (
      <div className={styles.upgradeSection}>
        <DropdownMenuItem onClick={openCheckout} className={styles.upgradeItem}>
          <Sparkles size={16} className={styles.menuIcon} />
          Passer Premium
        </DropdownMenuItem>
      </div>
    );
  }

  // Fallback: original tarifs link
  return (
    <DropdownMenuItem asChild>
      <Link to="/tarifs/">
        <CreditCard size={16} className={styles.menuIcon} />
        Tarifs
      </Link>
    </DropdownMenuItem>
  );
};

export const AppUserMenu = () => {
  const { authState, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    window.location.href = "/";
  };

  if (authState.type === "loading") {
    return <Skeleton className={styles.loadingSkeleton} />;
  }

  if (authState.type === "unauthenticated") {
    return (
      <DropdownMenu>
        <DropdownMenuTrigger className={styles.userTrigger} aria-label="Connexion">
          <User size={18} className={styles.userIcon} />
          <span className={styles.loginLabel}>Connexion</span>
          <ChevronDown size={14} className={styles.chevron} />
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem asChild>
            <Link to="/login/">
              <User size={16} className={styles.menuIcon} />
              Se connecter
            </Link>
          </DropdownMenuItem>
          <DropdownMenuItem asChild>
            <Link to="/inscription/">
              <CreditCard size={16} className={styles.menuIcon} />
              Créer un compte
            </Link>
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuLabel>Apparence</DropdownMenuLabel>
          <div className={styles.themeRow}>
            <ThemeSelector />
          </div>
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }

  const { user } = authState;
  const initial = user.displayName ? user.displayName.charAt(0).toUpperCase() : "?";

  return (
    <DropdownMenu>
      <DropdownMenuTrigger className={styles.userTrigger} aria-label="Menu utilisateur" data-tour="nav-profiles">
        <div className={styles.avatar}>
          <span className={styles.avatarInitial}>{initial}</span>
        </div>
        <ChevronDown size={14} className={styles.chevron} />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className={styles.menuContent}>
        <DropdownMenuLabel>Mon Compte</DropdownMenuLabel>
        <DropdownMenuSeparator />
        <UpgradeMenuItem />
        <DropdownMenuItem asChild>
          <Link to="/compte/">
            <User size={16} className={styles.menuIcon} />
            Mon Compte
          </Link>
        </DropdownMenuItem>
        <DropdownMenuItem asChild>
          <Link to="/contact/">
            <HelpCircle size={16} className={styles.menuIcon} />
            Aide / Contact
          </Link>
        </DropdownMenuItem>
        {user.role === "admin" && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuLabel>Administration</DropdownMenuLabel>
            <DropdownMenuItem asChild>
              <Link to="/espace-admin/">
                <Settings size={16} className={styles.menuIcon} />
                Espace Admin
              </Link>
            </DropdownMenuItem>
          </>
        )}
        <DropdownMenuSeparator />
        <DropdownMenuLabel>Apparence</DropdownMenuLabel>
        <div className={styles.themeRow}>
          <ThemeSelector />
        </div>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={handleLogout} className={styles.logoutItem}>
          <LogOut size={16} className={styles.menuIcon} />
          Se déconnecter
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
